import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Typography,
  Button,
  Input,
  Row,
  Col,
  Space,
  message,
  Card,
  Table,
  Tag,
  Grid,
  List,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Package, PackageStatus, PackageType, DeliveryService } from '@/types/residents/package';
import { getPackages, addPackage, updatePackage, deletePackage } from '@/api/residents';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import PackageModal from '../../components/features/residents/PackageModal';
import { useAuth } from '@/contexts/AuthContext';

const { useBreakpoint } = Grid;
const { Text } = Typography;

// Constants
const PAGE_SIZE = 10;
const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

const PackageTracking: React.FC = () => {
  const { t } = useTranslation(['packages', 'common']);
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [lastDocs] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingPackageId, setDeletingPackageId] = useState<string | null>(null);

  // Queries
  const { data, isLoading } = useQuery({
    queryKey: ['packages', page],
    queryFn: async () => {
      const lastDoc = page > 1 ? lastDocs[page - 1] : undefined;
      return getPackages(PAGE_SIZE, lastDoc, firestoreUser || undefined);
    },
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });

  // Mutations
  const addMutation = useMutation({
    mutationFn: (data: Omit<Package, 'id'>) => addPackage(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('packages:createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      setIsModalOpen(false);
    },
    onError: () => {
      message.error(t('packages:createError'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Package> }) =>
      updatePackage(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('packages:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      setIsModalOpen(false);
    },
    onError: () => {
      message.error(t('packages:updateError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePackage(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('packages:deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      setIsDeleteModalOpen(false);
      setDeletingPackageId(null);
    },
    onError: () => {
      message.error(t('packages:deleteError'));
    },
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingPackage(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((pkg: Package) => {
    setEditingPackage(pkg);
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback((pkg: Package) => {
    navigate(`/residents/packages/${pkg.id}`, { state: { package: pkg } });
  }, [navigate]);

  const handleDelete = useCallback((pkg: Package) => {
    setDeletingPackageId(pkg.id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleModalSuccess = useCallback((data: Omit<Package, 'id'>) => {
    if (editingPackage) {
      updateMutation.mutate({ id: editingPackage.id, data });
    } else {
      addMutation.mutate(data);
    }
  }, [editingPackage, updateMutation, addMutation]);

  const handleDeleteConfirm = useCallback(() => {
    if (deletingPackageId) {
      deleteMutation.mutate(deletingPackageId);
    }
  }, [deletingPackageId, deleteMutation]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Memoized values
  const filteredData = useMemo(() => {
    if (!data?.packages) return [];
    return data.packages.filter((pkg: Package) =>
      pkg.trackingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (pkg.resident?.name && pkg.resident.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (pkg.employee?.name && pkg.employee.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (pkg.sender?.name && pkg.sender.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [data?.packages, searchTerm]);

  const getStatusColor = (status: PackageStatus) => {
    const colors: Record<PackageStatus, string> = {
      pending: 'warning',
      received: 'processing',
      notified: 'cyan',
      collected: 'success',
      expired: 'error',
      returned: 'default',
    };
    return colors[status];
  };

  const getTypeColor = (type: PackageType) => {
    const colors: Record<PackageType, string> = {
      regular: 'blue',
      express: 'green',
      fragile: 'orange',
      oversized: 'purple',
      other: 'default',
    };
    return colors[type];
  };

  const getDeliveryServiceColor = (service: DeliveryService) => {
    const colors: Record<DeliveryService, string> = {
      fedex: 'purple',
      ups: 'brown',
      dhl: 'red',
      usps: 'blue',
      amazon: 'orange',
      other: 'default',
    };
    return colors[service];
  };

  const renderPackageCard = (pkg: Package) => (
    <Card
      key={pkg.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleView(pkg)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(pkg)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(pkg)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={
          <Space>
            <Text strong>{pkg.trackingNumber}</Text>
            <Tag color={getTypeColor(pkg.type)}>
              {t(`packages:types.${pkg.type}`)}
            </Tag>
          </Space>
        }
        description={
          <Space direction="vertical" size="small">
            <div>
              <Tag color={getDeliveryServiceColor(pkg.deliveryService)}>
                {t(`packages:deliveryServices.${pkg.deliveryService}`)}
              </Tag>
              <Tag color={getStatusColor(pkg.status)}>
                {t(`packages:statuses.${pkg.status}`)}
              </Tag>
            </div>
            <div>{t('packages:resident')}: {pkg.resident?.name || '-'}</div>
            <div>{t('packages:receivedAt')}: {pkg.receivedAt ? dayjs(pkg.receivedAt).format('YYYY-MM-DD HH:mm') : '-'}</div>
          </Space>
        }
      />
    </Card>
  );

  const columns = [
    {
      title: t('packages:trackingNumber'),
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      render: (text: string, record: Package) => (
        <Space>
          <Text strong>{text}</Text>
          <Tag color={getTypeColor(record.type)}>
            {t(`packages:types.${record.type}`)}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('packages:deliveryService'),
      dataIndex: 'deliveryService',
      key: 'deliveryService',
      render: (service: DeliveryService) => (
        <Tag color={getDeliveryServiceColor(service)}>
          {t(`packages:deliveryServices.${service}`)}
        </Tag>
      ),
    },
    {
      title: t('packages:status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: PackageStatus) => (
        <Tag color={getStatusColor(status)}>
          {t(`packages:statuses.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('packages:resident'),
      dataIndex: ['resident', 'name'],
      key: 'resident',
      render: (name: string) => name || '-',
    },
    {
      title: t('packages:receivedAt'),
      dataIndex: 'receivedAt',
      key: 'receivedAt',
      render: (date: Date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a: Package, b: Package) => 
        (a.receivedAt?.getTime() ?? 0) - (b.receivedAt?.getTime() ?? 0),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      width: screens.xs ? undefined : 200,
      render: (_: any, record: Package) => (
        <Space wrap={!screens.xs} direction={screens.xs ? 'vertical' : 'horizontal'}>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size={screens.xs ? 'small' : 'middle'}
          >
            {!screens.xs && t('common:actions.view')}
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size={screens.xs ? 'small' : 'middle'}
          >
            {!screens.xs && t('common:actions.edit')}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            size={screens.xs ? 'small' : 'middle'}
          >
            {!screens.xs && t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input
              placeholder={t('packages:searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              prefix={<SearchOutlined />}
              style={{ width: screens.xs ? '100%' : 300 }}
            />
            <Button type="primary" onClick={handleAdd} icon={<PlusOutlined />}>
              {t('packages:add')}
            </Button>
          </Space>
        </Col>
      </Row>

      
      {screens.xs ? (
        <List
          dataSource={filteredData}
          renderItem={renderPackageCard}
          rowKey="id"
          loading={isLoading}
          grid={{ gutter: 16, xs: 1, sm: 1 }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data?.packages?.length || 0,
            onChange: (newPage) => setPage(newPage),
            showSizeChanger: false,
          }}
          scroll={{ x: 'max-content' }}
        />
      )}

      {/* Modals */}
      <PackageModal
        open={isModalOpen}
        package={editingPackage}
        onSuccess={handleModalSuccess}
        onCancel={() => setIsModalOpen(false)}
      />

      <DeleteConfirmationModal
        visible={isDeleteModalOpen}
        title={t('packages:deleteConfirmTitle')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setDeletingPackageId(null);
        }}
      />
    </div>
  );
};

export default PackageTracking;